import { createImageRequest, freezeRequest } from './generation-request.js';

// Dedicated workflows in the active image catalog, also supported by SDK 5.60.3.
export const IMAGE_ACTIONS = Object.freeze({
  upscale: { modelId: 'rtx_vsr_pro', label: 'Upscale', description: 'Enlarge the original with NVIDIA RTX VSR. No creative prompt is used.' },
  background: { modelId: 'birefnet_image_background_removal_fp16', label: 'Remove background', description: 'Keep the foreground and return a transparent PNG at the original size.' },
  cutout: { modelId: 'sam3_image_segment_bf16', label: 'Cut out an object', description: 'Describe the object to keep. SAM 3 returns a transparent PNG at the original size.' }
});

// Same 8px alignment and minimum edge as the supported RTX VSR tool.
export function resolveUpscaleDimensions(source, longestEdge, model) {
  const target = Number(longestEdge);
  const widthRange = model.parameters.width;
  const heightRange = model.parameters.height;
  const longest = Math.max(source.width, source.height);
  if (!Number.isFinite(target) || target <= longest) throw new Error('Choose an output larger than the original image.');
  const scale = target / longest;
  const width = Math.floor(source.width * scale / widthRange.step) * widthRange.step;
  const height = Math.floor(source.height * scale / heightRange.step) * heightRange.step;
  if (width < widthRange.min || height < heightRange.min || width > widthRange.max || height > heightRange.max) {
    throw new Error(`This aspect ratio needs both output edges between ${widthRange.min} and ${widthRange.max}px. Choose another size.`);
  }
  if (width < source.width || height < source.height || width === source.width && height === source.height) {
    throw new Error('Choose a larger size so the aligned output enlarges the original without reducing either edge.');
  }
  return { width, height };
}

/** Source is readImageReference's native Blob and dimensions, never a thumbnail. */
export function buildImageActionConfig(source, action, settings, model) {
  const definition = IMAGE_ACTIONS[action];
  if (!definition || model?.id !== definition.modelId) throw new Error('Load a supported image action from the current catalog.');
  if (!model.availableNetworks.includes(settings.network)) throw new Error('This action is unavailable on the selected network.');
  const output = action === 'upscale' ? resolveUpscaleDimensions(source, settings.longestEdge, model)
    : { width: source.width, height: source.height };
  const dimensions = action === 'upscale' ? output
    : { width: model.parameters.width.default, height: model.parameters.height.default };
  const format = action === 'upscale' ? settings.outputFormat : 'png';
  if (!['png', 'jpg'].includes(format)) throw new Error('Choose PNG or JPEG output.');
  const projectConfig = {
    type: 'image', modelId: model.id, network: settings.network, sizePreset: 'custom', ...dimensions,
    steps: model.parameters.steps.default, positivePrompt: '', negativePrompt: '', stylePrompt: '',
    numberOfImages: 1, numberOfMedia: 1, numberOfPreviews: 0, outputFormat: format,
    startingImage: source.blob, startingImageStrength: 1, tokenType: 'spark', disableNSFWFilter: true,
    ...(action === 'background' ? { applyMask: true } : {}),
    ...(action === 'cutout' ? { sam3Prompt: { text: settings.object.trim(), applyMask: true, maxInstances: 1 } } : {})
  };
  if (action === 'cutout' && !projectConfig.sam3Prompt.text) throw new Error('Describe the object to keep.');
  return { projectConfig, output };
}

export async function createImageActionRequest({ favorite, source, action, settings, model, context }) {
  const { projectConfig, output } = buildImageActionConfig(source, action, settings, model);
  const request = await createImageRequest({
    prompts: [''], modelIds: [model.id], imagesPerPrompt: 1, seedSet: [1], ...context,
    billingMode: settings.billingMode || 'auto', getModel: () => model, resolveModelParams: () => projectConfig
  });
  return freezeRequest({ ...request, kind: 'image-action', action, sourceFavoriteId: favorite.id,
    sourcePrompt: favorite.prompt || '', sourceModel: favorite.model || '',
    sourceSensitive: favorite.sensitive === true,
    sourceDimensions: { width: source.width, height: source.height }, outputDimensions: output,
    actionSettings: { ...settings }, projectConfig: request.slots[0].projectConfig });
}

/** An explicit retry gets new lineage and current billing, retaining the exact source/settings/seed. */
export async function renewImageActionRequest(previous, model, context, billingMode = 'auto') {
  const next = await createImageRequest({ prompts: [''], modelIds: [model.id], imagesPerPrompt: 1,
    seedSet: [previous.projectConfig.seed], ...context, billingMode, getModel: () => model,
    resolveModelParams: () => previous.projectConfig });
  return freezeRequest({ ...previous, ...next, projectConfig: next.slots[0].projectConfig });
}

export const isImageActionUnresolved = record => !!record && !['saved', 'failed', 'canceled'].includes(record.status);

export function imageActionUnverified(message, projectId) {
  return Object.assign(new Error(message), { code: 'IMAGE_ACTION_UNVERIFIED', unverified: true, ...(projectId ? { projectId } : {}) });
}

/** `labelled`: the server's own `nsfwDetected` label on the delivered result, when the live job carried it. */
export function imageActionResultFavorite(record, blob, resultUrl, dimensions, labelled = false) {
  const config = record.request.projectConfig;
  return {
    id: record.attemptId, blob, originalUrl: resultUrl, prompt: record.request.sourcePrompt,
    model: config.modelId, requestedModel: config.modelId, projectId: record.projectId,
    requestId: record.request.id, seed: config.seed, width: dimensions.width, height: dimensions.height,
    steps: config.steps, savedAt: Date.now(), completedAt: Date.now(),
    sourceFavoriteId: record.favoriteId, imageAction: record.request.action,
    actionSettings: record.request.actionSettings, sourceModel: record.request.sourceModel,
    sourceDimensions: record.request.sourceDimensions,
    // An upscale, cutout or background removal of a labelled favorite stays labelled,
    // and a result the server labels itself is labelled too.
    sensitive: record.request.sourceSensitive === true || labelled === true
  };
}
